import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import GetCookie from "../../../../utils/GetCookie";
import ProcessingPopup from "../../../../utils/popup/ProcessingPopup";
import ErrorPopup from "../../../../utils/popup/ErrorPopup";
import SuccessPopup from "../../../../utils/popup/SuccessPopup";
import DeleteConfirmationModal from "../../../../utils/DeleteConfirmationModal";
import ConfirmationModal from "../../../../utils/ConfirmationModal";
import DocumentPreview from "../../../../utils/DocumentPreview";
import TrimContent from "../../../../utils/TrimContent";
import PaginationControls from "../../../../utils/PaginationControls";
import { setSLRContext, setActiveSearch } from "../../../../../store/literatureDiscoverySlice";
import { normalizePicoJson, buildTagOptionsFromPicoJson } from "../../../../utils/Picocolors";
import TagFilterPanel from "./TagFilterPanel";
import YearRangePicker from "../../../../utils/YearRangePicker";


/* A native <select> renders its list through the OS and ignores the app's
   styling, so this is a toggle button plus a floating card of options: light
   rows, the selected one filled blue, at most 20vh tall and scrollable.
   Closes on an outside click or Esc. Same as AbstractsList, plus per-option
   `disabled` / `title` (used by the AI match scope picker). */
const InlineSelect = ({ id, openId, setOpenId, value, options = [], placeholder = "Select", width, className = "" }) => {
  const [localOpen, setLocalOpen] = useState(false);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const controlled = typeof setOpenId === "function";
  const isOpen = controlled ? openId === id : localOpen;
  const setOpen = (next) => {
    if (controlled) setOpenId(next ? id : null);
    else setLocalOpen(next);
  };
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!isOpen) return undefined;

    // Bring the selected option into view inside the list (without
    // scrolling the filter panel itself).
    const list = listRef.current;
    const active = list?.querySelector('[data-selected="true"]');
    if (list && active) {
      list.scrollTop = active.offsetTop - list.clientHeight / 2 + active.offsetHeight / 2;
    }

    const onMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <div ref={rootRef} className={`position-relative ${className}`} style={width ? { width } : undefined}>
      <button
        type="button"
        className="ev-btn-light-fill ev-btn-small ev-radius-xsmall w-100 d-flex align-items-center justify-content-between"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setOpen(!isOpen)}
      >
        <span className="text-truncate">{selected ? selected.label : placeholder}</span>
        <i className={`bi bi-chevron-${isOpen ? "up" : "down"} ms-1`}></i>
      </button>
      {isOpen && (
        <div
          ref={listRef}
          role="listbox"
          className="position-absolute start-0 mt-1 p-2 bg-white border border-primary ev-radius-xsmall shadow-sm d-flex flex-column gap-1 scrollable-element"
          style={{
            top: "100%",
            zIndex: 1000,
            minWidth: "100%",
            width: "max-content",
            maxHeight: "20vh",
            overflowY: "auto",
          }}
        >
          {options.map((opt) => {
            const isSelected = value === opt.value;
            return (
              <button
                key={String(opt.value)}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-selected={isSelected ? "true" : undefined}
                className={`${isSelected ? "ev-btn-blue-fill-round" : "ev-btn-light-fill"} ev-btn-small ev-radius-xsmall w-100 text-start text-nowrap flex-shrink-0 ${opt.disabled ? "opacity-50" : ""}`}
                style={opt.disabled ? { cursor: "not-allowed" } : undefined}
                disabled={!!opt.disabled}
                title={opt.title}
                onClick={() => { if (opt.disabled) return; opt.onSelect?.(); setOpen(false); }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ADV_FIELD_OPTIONS = [
  { value: "", label: "Field" },
  { value: "title", label: "Title" },
  { value: "abstract", label: "Abstract" },
  { value: "author", label: "Author" },
  { value: "year", label: "Year" },
  { value: "population", label: "Population" },
  { value: "intervention", label: "Intervention" },
  { value: "comparison", label: "Comparator" },
  { value: "outcome", label: "Outcome" },
  { value: "study_design", label: "Study Design" },
  { value: "exclusion", label: "Exclusion" },
];

const ADV_OPERATOR_OPTIONS = [
  { value: "contains", label: "Contains" },
  { value: "not_contains", label: "Does not contain" },
  { value: "equals", label: "Equals" },
  { value: "not_equals", label: "Does not equal" },
  { value: "begins_with", label: "Starts with" },
  { value: "not_begins_with", label: "Does not start with" },
  { value: "ends_with", label: "Ends with" },
  { value: "not_ends_with", label: "Does not end with" },
  { value: "is_empty", label: "Is empty" },
  { value: "is_not_empty", label: "Is not empty" },
  { value: "is_null", label: "Is null" },
  { value: "is_not_null", label: "Is not null" },
];

const LinkFullText = ({ fabricId, picoJson = null, projectName = "" }) => {
  const API_BASE_URL = "/pceapi/slr/phase-linking";
  const FT_URL       = "/pceapi/slr";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const headers = useMemo(
    () => ({
      Authorization: GetCookie("access_token"),
    }),
    [],
  );

  // ===== DATA STATES =====
  const [studies, setStudies] = useState([]);
  const [metadata, setMetadata] = useState({
    start_year: null,
    end_year: null,
    category: "ALL",
    tags: {},
    total_studies: 0,
    Linked: 0,
    Unlinked: 0,
    WithDecisions: 0,
    unstructured_thread_id: null,
    data_fabric_id: null,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    size: 25,
    total: 0,
    total_pages: 1,
    has_more: false,
  });

  // ===== FILTER STATES =====
  const [filters, setFilters] = useState({
    page: 1,
    size: 25,
    start_year: null,
    end_year: null,
    category: "ALL",
    sort_order: "desc",
    search: null,
  });

  // ===== UI STATES =====
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [expandedAbstracts, setExpandedAbstracts] = useState({});
  const [aiMatchScope, setAiMatchScope] = useState(false);
  const [aiMappingResults, setAiMappingResults] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [sourceSearches, setSourceSearches] = useState([]);
  const [showSourceSearchesDropdown, setShowSourceSearchesDropdown] = useState(false);
  const [migrateConfirmSearch, setMigrateConfirmSearch] = useState(null);
  const sourceSearchesRef = useRef(null);

  // ===== BULK UPLOAD MODAL STATES =====
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkModalStudy, setBulkModalStudy] = useState(null);
  const [bulkDocs, setBulkDocs] = useState([]);
  const [bulkDocsLoading, setBulkDocsLoading] = useState(false);
  const [bulkDocsPagination, setBulkDocsPagination] = useState({
    next_offset: null,
    has_more: false,
    total_docs: 0,
  });
  const [bulkSearchInput, setBulkSearchInput] = useState("");

  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [showAllTags, setShowAllTags] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [section1Open, setSection1Open] = useState(false);
  const [section2Open, setSection2Open] = useState(false);
  const [section3Open, setSection3Open] = useState(false);
  const [advancedEnabled, setAdvancedEnabled] = useState(false);
  const [advConditions, setAdvConditions] = useState([
    { id: 1, category: "", filterOp: "contains", connector: "AND", value: "" },
  ]);
  // Which advanced-filter dropdown is expanded, keyed "<ruleId>:<which>".
  const [advOpenDropdown, setAdvOpenDropdown] = useState(null);

  const activeFilterCount =
    (filters.search ? 1 : 0) +
    (filters.start_year ? 1 : 0) +
    (filters.end_year ? 1 : 0) +
    (filters.category && filters.category !== "ALL" ? 1 : 0) +
    (selectedTagIds?.length || 0) +
    (advancedEnabled ? advConditions.filter(c => c.category || c.value).length : 0);

  const clearAllFilters = () => {
    setSearchInput("");
    setSelectedTagIds([]);
    setAdvancedEnabled(false);
    setAdvConditions([{ id: 1, category: "", filterOp: "contains", connector: "AND", value: "" }]);
    const cleared = {
      ...filters,
      page: 1,
      start_year: null,
      end_year: null,
      category: "ALL",
      search: null,
    };
    setFilters(cleared);
    fetchStudiesList(cleared, []); // overrideTagIds=[] — avoids stale selectedTagIds closure
  };

  const addAdvCondition = () =>
    setAdvConditions((prev) => [
      ...prev,
      { id: Date.now(), category: "", filterOp: "contains", connector: "AND", value: "" },
    ]);
  const removeAdvCondition = (id) =>
    setAdvConditions((prev) => prev.filter((c) => c.id !== id));
  const updateAdvCondition = (id, field, val) =>
    setAdvConditions((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: val } : c))
    );

  const [unsavedMappings, setUnsavedMappings] = useState({});
  useEffect(() => {
    if ((metadata?.WithDecisions || 0) > 0 && aiMatchScope) setAiMatchScope(false);
  }, [metadata?.WithDecisions]); // eslint-disable-line
  // Confirmation before actions that delete full-text screening decisions
  const [docChangeConfirmCount, setDocChangeConfirmCount] = useState(0);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewDocName, setPreviewDocName] = useState("");

  const fileInputRef = useRef(null);

  // ===== POPUP STATES =====
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState("");
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState("");

  const closeErrorPopup = () => setIsErrorPopupVisible(false);
  const closeSuccessPopup = () => setIsSuccessPopupVisible(false);

  // ===== CONFIG ID RESOLUTION =====
  // Read config_id directly from Redux — it is already resolved by
  // fetchProjectDetails in ScreeningSpaceHome and is guaranteed to be
  // slr_fabric_config.id. This avoids a redundant get-project-details call
  // that was intermittently returning data_fabric.id as a fallback, causing
  // phase-linking/list to query with the wrong fabric id (0 results).
  const { projectDetails } = useSelector((state) => state.screeningSpace);
  const configFabricId = projectDetails?.config_id || fabricId;

  // "Bibliographic" badge: study_ids of papers added via Full-Text QC → Add Paper
  const [bibliographicIds, setBibliographicIds] = useState(() => new Set());
  useEffect(() => {
    if (!fabricId) return;
    let cancelled = false;
    axios.post(`${FT_URL}/qc/bibliographic-study-ids`, { slr_fabric_config_id: fabricId }, { headers })
      .then((res) => {
        if (!cancelled && res.data?.status === "success") {
          setBibliographicIds(new Set((res.data.study_ids || []).map(String)));
        }
      })
      .catch((err) => console.error("Bibliographic ids fetch failed:", err));
    return () => { cancelled = true; };
  }, [fabricId]); // eslint-disable-line

  // Filter is hidden when there are no bibliographic papers — never leave it stuck on BIBLIOGRAPHIC
  useEffect(() => {
    if (bibliographicIds.size === 0 && filters.category === "BIBLIOGRAPHIC") handleCategoryChange("ALL");
  }, [bibliographicIds, filters.category]); // eslint-disable-line

  // ===== DELETE CONFIRMATION =====
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);

  const handleOpenPreview = (doc) => {
    setPreviewDocId(doc.doc_id);
    setPreviewDocName(doc.name || doc.title);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewDocId(null);
    setPreviewDocName("");
  };

  const startProcessing = (msg) => {
    setProcessingMessage(msg || "Processing...");
    setIsProcessingPopupVisible(true);
  };

  const stopProcessing = () => {
    setIsProcessingPopupVisible(false);
    setProcessingMessage("");
  };

  const PICO_COLORS = {
    population: "#3b4eff",
    intervention: "#4aa3df",
    comparison: "#d65a1f",
    outcome: "#7a1e6c",
    study_design: "#2c7a4b",
    exclusion: "#DC3545",
  };

  const normalizePicoKey = (key) => {
    const k = String(key || "").trim().toLowerCase();
    if (!k) return "other";
    if (k.startsWith("pop")) return "population";
    if (k.startsWith("int")) return "intervention";
    if (k.startsWith("comp")) return "comparison";
    if (k.startsWith("out")) return "outcome";
    if (k.includes("study")) return "study_design";
    if (k.includes("exclu")) return "exclusion";
    return "other";
  };

  // Build tagOptions for TagFilterPanel
  const tagOptions = useMemo(() => buildTagOptionsFromPicoJson(picoJson), [picoJson]);

  // ===== COMPUTED VALUES =====
  const safeStudies = Array.isArray(studies) ? studies : [];
  const unsavedCount = Object.keys(unsavedMappings).length;
  const totalPages = pagination.total_pages || 1;

  // ===== API: Fetch Studies List =====
  const fetchStudiesList = async (customFilters = null, overrideTagIds = null) => {
      if (!configFabricId) return;
      setLoading(true);
      setProcessingMessage("Loading studies...");
      setIsProcessingPopupVisible(true);
      try {
        const requestFilters = customFilters || filters;
        // selectedTagIds is managed separately from filters (same pattern as Screening.jsx).
        // overrideTagIds is passed only by clearAllFilters to avoid stale closure.
        const effectiveTagIds = overrideTagIds !== null ? overrideTagIds : selectedTagIds;
        const tagTexts = tagOptions
          .filter((t) => effectiveTagIds.includes(t.id))
          .map((t) => t.tag);
        const res = await axios.post(
          `${API_BASE_URL}/list`,
          {
            slr_fabric_id: configFabricId,
            page: requestFilters.page,
            size: requestFilters.size,
            start_year: requestFilters.start_year || null,
            end_year: requestFilters.end_year || null,
            category: requestFilters.category,
            selected_tags: tagTexts.length > 0 ? tagTexts : null,
            sort_order: requestFilters.sort_order,
            search: requestFilters.search,
            advanced_filters: advancedEnabled && advConditions.some(c => c.category || c.value)
              ? { rules: advConditions.filter(c => c.category || c.value).map(c => ({ field: c.category, operator: c.filterOp || "contains", value: c.value, connector: c.connector || "AND" })) }
              : null,
          },
          { headers },
        );
        if (res.data?.status === "success") {
          const inner = res.data?.data || {};
          const documents = inner?.documents;
          const counts = inner?.counts || {};
          const meta = inner?.metadata || {};
          const pag = inner?.pagination || {};
          const safeList = Array.isArray(documents) ? documents : [];
          setStudies(safeList);
          const total = counts?.total ?? pag?.total ?? 0;
          const pageNo = pag?.page ?? requestFilters.page ?? 1;
          const pageSize = pag?.size ?? requestFilters.size ?? 25;
          const totalPagesCalc =
            pag?.total_pages ?? (total ? Math.ceil(total / pageSize) : 1);
          setMetadata({
            start_year: meta?.start_year ?? null,
            end_year: meta?.end_year ?? null,
            category: meta?.category ?? "ALL",
            tags: meta?.tags || {},
            total_studies: total,
            Linked: counts?.linked ?? 0,
            Unlinked: counts?.unlinked ?? 0,
            WithDecisions: counts?.with_fulltext_decisions ?? 0,
            unstructured_thread_id: meta?.unstructured_thread_id ?? null,
            data_fabric_id: meta?.data_fabric_id ?? null,
          });
          setSourceSearches(meta?.source_searches ?? []);
          setPagination({
            page: pageNo,
            size: pageSize,
            total: total,
            total_pages: totalPagesCalc || 1,
            has_more: pageNo < (totalPagesCalc || 1),
          });
        } else {
          setPopupErrorMessage(res.data?.message || "Failed to fetch studies");
          setIsErrorPopupVisible(true);
        }
      } catch (err) {
        console.error("Error fetching studies:", err);
        setPopupErrorMessage("Failed to load studies list");
        setIsErrorPopupVisible(true);
      } finally {
        setLoading(false);
        setIsProcessingPopupVisible(false);
        setProcessingMessage("");
      }
  };

  // ===== API: Upload From Device =====
  const handleFileUploadFromDevice = async (event, study) => {
    const file = event.target.files?.[0];
    if (!file || !fabricId || !study) return;
    if (file.type !== "application/pdf") {
      setPopupErrorMessage("Please upload only PDF files.");
      setIsErrorPopupVisible(true);
      event.target.value = null;
      return;
    }
    const formData = new FormData();
    formData.append("pdf_file", file);
    formData.append("slr_fabric_config_id", fabricId);
    setIsProcessingPopupVisible(true);
    setProcessingMessage("Uploading and processing PDF...");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/upload-single-pdf`,
        formData,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } },
      );
      if (res.data?.status === "success") {
        setUnsavedMappings((prev) => ({
          ...prev,
          [study.phase1_id]: {
            doc_id: res.data.doc_id,
            fileName: file.name,
            isAIMatched: false,
            prevDocId: study.doc_id_linked ?? null,
            hasDecisions: !!study.has_fulltext_decisions,
          },
        }));
        setPopupSuccessMessage(
          `File "${file.name}" uploaded. Don't forget to Save All Selections.`,
        );
        setIsSuccessPopupVisible(true);
      } else {
        setPopupErrorMessage(res.data?.message || "Upload failed");
        setIsErrorPopupVisible(true);
      }
    } catch (err) {
      console.error("Upload error:", err);
      setPopupErrorMessage("Error uploading file. Please try again.");
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      setProcessingMessage("");
      event.target.value = null;
    }
  };

  // ===== API: AI Mapping Start =====
  const handleFindMatches = async (retrigger = false) => {
    if (!fabricId) return;
    setIsProcessingPopupVisible(true);
    setProcessingMessage("Finding AI matches... This may take a few minutes.");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/ai-mapping-start`,
        { slr_project_id: fabricId, retrigger: aiMatchScope },
        { headers },
      );
      if (res.data?.status === "success") {
        const aiResults = res.data.linking_results || [];
        const newUnsaved = {};
        const aiResultsMap = {};
        const linkedByPaper = {};
        const decisionsByPaper = {};
        studies.forEach((s) => {
          linkedByPaper[s.phase1_id] = s.doc_id_linked ?? null;
          decisionsByPaper[s.phase1_id] = !!s.has_fulltext_decisions;
        });
        aiResults.forEach((link) => {
          aiResultsMap[link.p1_id] = link.doc_id_p2;
          newUnsaved[link.p1_id] = {
            doc_id: link.doc_id_p2,
            fileName: `Doc_${link.doc_id_p2}.pdf`,
            isAIMatched: true,
            prevDocId: link.prev_doc_id ?? linkedByPaper[link.p1_id] ?? null,
            // Papers not on the current page are unknown: assume decisions
            // may exist so the warning still shows.
            hasDecisions:
              link.p1_id in decisionsByPaper ? decisionsByPaper[link.p1_id] : true,
          };
        });
        setAiMappingResults(aiResultsMap);
        setUnsavedMappings((prev) => ({ ...prev, ...newUnsaved }));
        setPopupSuccessMessage(
          res.data.message ||
            `AI matching completed: ${res.data.successful_links || aiResults.length} links found`,
        );
        await fetchStudiesList();
      } else {
        setPopupErrorMessage(res.data?.message || "AI matching failed");
        setIsErrorPopupVisible(true);
      }
    } catch (err) {
      console.error("AI mapping error:", err);
      setPopupErrorMessage(
        err.response?.data?.message || "Failed to run AI matching",
      );
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      setProcessingMessage("");
    }
  };

  // ===== API: Fetch Bulk Docs (shared helper) =====
  const fetchBulkDocs = async (study, search = "", offset = 0, append = false) => {
    setBulkDocsLoading(true);
    startProcessing(offset === 0 ? "Loading bulk uploaded documents..." : "Loading more documents...");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/select-from-bulk-uploads`,
        {
          abstract_id: study.phase1_id,
          slr_project_id: fabricId,
          offset,
          size: 10,
          selected_doc_id: offset === 0
            ? (aiMappingResults?.[study.phase1_id] || unsavedMappings[study.phase1_id]?.doc_id || study.doc_id_linked || null)
            : undefined,
          search: search.trim() || null,
        },
        { headers },
      );
      if (res.data?.status === "success") {
        setBulkDocs((prev) =>
          append ? [...prev, ...(res.data.p2_lists || [])] : (res.data.p2_lists || [])
        );
        setBulkDocsPagination({
          next_offset: res.data.next_offset,
          has_more: res.data.has_more,
          total_docs: res.data.total_docs,
        });
      } else {
        setPopupErrorMessage(res.data?.message || "Failed to load documents");
        setIsErrorPopupVisible(true);
      }
    } catch (err) {
      console.error("Bulk uploads error:", err);
      setPopupErrorMessage("Failed to load bulk upload documents");
      setIsErrorPopupVisible(true);
    } finally {
      setBulkDocsLoading(false);
      stopProcessing();
    }
  };

  // ===== API: Select From Bulk Uploads =====
  const handleOpenBulkModal = async (study) => {
    setBulkModalStudy(study);
    setShowBulkModal(true);
    setBulkDocs([]);
    setBulkSearchInput("");
    setBulkDocsLoading(true);

    const aiMatchedDocId = aiMappingResults?.[study.phase1_id] || null;
    const unsavesDocId = unsavedMappings[study.phase1_id]?.doc_id || null;
    const linkedDocId = study.doc_id_linked || null;
    const preSelectedDocId = aiMatchedDocId || unsavesDocId || linkedDocId;
    setSelectedDocId(preSelectedDocId);
    if (aiMatchedDocId) setSelectedDocId(aiMatchedDocId);

    await fetchBulkDocs(study, "", 0, false);
  };

  // ===== API: Load More Bulk Docs =====
  const loadMoreBulkDocs = async () => {
    if (!bulkDocsPagination.has_more || !bulkModalStudy) return;
    await fetchBulkDocs(bulkModalStudy, bulkSearchInput, bulkDocsPagination.next_offset, true);
  };

  const handleConfirmDeleteBulkDoc = () => {
    if (!docToDelete) return;
    setBulkDocs((prev) => prev.filter((d) => d.doc_id !== docToDelete.doc_id));
    if (selectedDocId === docToDelete.doc_id) setSelectedDocId(null);
    setShowDeleteConfirm(false);
    setDocToDelete(null);
    setPopupSuccessMessage("Document removed successfully");
    setIsSuccessPopupVisible(true);
  };

  // ===== Save selected doc into unsavedMappings =====
  // Selecting a document is only staged until "Save All Selection", but warn
  // right away when the choice will cost something.
  const [pendingDocSelect, setPendingDocSelect] = useState(null);

  const handleSelectDocument = (doc) => {
    if (!bulkModalStudy) return;
    const currentDoc = bulkModalStudy.doc_id_linked ?? null;
    const changingDoc = currentDoc != null && String(currentDoc) !== String(doc.doc_id);
    const loseDecisions = changingDoc && !!bulkModalStudy.has_fulltext_decisions;
    const otherStudies = (doc.linked_to_other_studies || []);
    if (changingDoc || otherStudies.length > 0) {
      setPendingDocSelect({ doc, study: bulkModalStudy, changingDoc, loseDecisions, otherStudies });
      // Close the picker so the confirmation isn't stacked on top of it.
      setShowBulkModal(false);
      return;
    }
    applySelectDocument(doc, bulkModalStudy);
  };

  const applySelectDocument = (doc, study) => {
    if (!study) return;
    setUnsavedMappings((prev) => ({
      ...prev,
      [study.phase1_id]: {
        doc_id: doc.doc_id,
        fileName: doc.name || doc.title,
        isAIMatched: false,
        prevDocId: study.doc_id_linked ?? null,
        hasDecisions: !!study.has_fulltext_decisions,
      },
    }));
    setShowBulkModal(false);
    setBulkModalStudy(null);
    setPopupSuccessMessage(
      `Document "${doc.name}" selected for linking. Click "Save All Selection" to apply it.`,
    );
    setIsSuccessPopupVisible(true);
  };

  // ===== Confirm before deleting full-text decisions =====
  // Papers whose already-linked document is being replaced. Saving these
  // deletes their full-text screening decisions on the backend.
  const countDocChanges = () =>
    Object.values(unsavedMappings).filter(
      (m) =>
        m.hasDecisions !== false &&
        m.prevDocId != null &&
        String(m.prevDocId) !== String(m.doc_id),
    ).length;

  const requestSaveAllMappings = () => {
    const changed = countDocChanges();
    if (changed > 0) {
      setDocChangeConfirmCount(changed);
      return;
    }
    handleSaveAllMappings();
  };

  // Restarting AI mapping for all papers drops every link (and with it all
  // full-text decisions), so it is blocked once any decision exists.
  const restartLocked = (metadata?.WithDecisions || 0) > 0;
  const RESTART_LOCK_MESSAGE =
    "AI mapping can't be restarted for all papers because full-text screening decisions already exist. Use \"Unlinked\", or change documents one by one.";

  const requestFindMatches = () => {
    // "All" re-matches papers that are already linked, which can replace
    // their documents and delete their full-text decisions.
    if (aiMatchScope && restartLocked) {
      setPopupErrorMessage(RESTART_LOCK_MESSAGE);
      setIsErrorPopupVisible(true);
      return;
    }
    handleFindMatches(false);
  };

  // ===== API: Save All Unsaved Mappings =====
  const handleSaveAllMappings = async () => {
    const mappingsToSave = Object.entries(unsavedMappings).map(
      ([phase1_id, data]) => ({
        paper_id: parseInt(phase1_id, 10),
        pdf_id: data.doc_id,
      }),
    );
    if (mappingsToSave.length === 0) {
      setPopupErrorMessage("No unsaved selections to save");
      setIsErrorPopupVisible(true);
      return;
    }
    setIsProcessingPopupVisible(true);
    setProcessingMessage(`Saving ${mappingsToSave.length} PDF links...`);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/save-pdf-linking`,
        { fabric_id: fabricId, mappings: mappingsToSave },
        { headers },
      );
      if (res.data?.status === "success" || res.data?.status === "partial") {
        const resetCount = res.data.fulltext_screening_reset_count || 0;
        const baseMsg = res.data.message || "PDF links saved successfully";
        setPopupSuccessMessage(
          resetCount > 0 && !/clear|delet|reset/i.test(baseMsg)
            ? `${baseMsg}. Full-text decisions were cleared for ${resetCount} paper(s).`
            : baseMsg,
        );
        setIsSuccessPopupVisible(true);
        setUnsavedMappings({});
        setAiMappingResults(null);
        await fetchStudiesList();
      } else {
        setPopupErrorMessage(res.data?.message || "Failed to save PDF links");
        setIsErrorPopupVisible(true);
      }
    } catch (err) {
      console.error("Save mappings error:", err);
      setPopupErrorMessage("Failed to save PDF links");
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      setProcessingMessage("");
    }
  };

  // ===== FILTER HANDLERS =====
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value, page: 1 };
    setFilters(newFilters);
    fetchStudiesList(newFilters);
  };

  const handleCategoryChange = (category) => {
    handleFilterChange("category", category);
  };

  // Tag toggles update state only — fetch is triggered by useEffect([selectedTagIds])
  // same pattern as Screening.jsx, avoids N parallel API calls (race condition).
  const handleTagToggle = (tagId) => {
    if (!tagId) return;
    setSelectedTagIds((prev) => {
      const s = new Set(prev || []);
      if (s.has(tagId)) s.delete(tagId); else s.add(tagId);
      return Array.from(s);
    });
  };

  // Group toggle — single state update, single subsequent fetch via useEffect
  const handleSetTagIds = (newIds) => {
    setSelectedTagIds(newIds);
  };

  // ===== PAGE NAVIGATION HANDLERS =====
  const handlePagePrev = () => {
    if (filters.page > 1) {
      const newFilters = { ...filters, page: filters.page - 1 };
      setFilters(newFilters);
      fetchStudiesList(newFilters);
    }
  };

  const handlePageNext = () => {
    if (filters.page < totalPages) {
      const newFilters = { ...filters, page: filters.page + 1 };
      setFilters(newFilters);
      fetchStudiesList(newFilters);
    }
  };

  const handlePageSizeChange = (newSize) => {
    const newFilters = { ...filters, size: newSize, page: 1 };
    setFilters(newFilters);
    fetchStudiesList(newFilters);
  };

  const handleRefresh = () => {
    setAiMappingResults(null);
    fetchStudiesList();
  };

  const toggleAbstract = (id) => {
    setExpandedAbstracts((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // ===== EFFECTS =====
  // Fetch once configFabricId is available (derived from Redux, so ready immediately
  // after projectDetails.config_id is set by fetchProjectDetails).
  useEffect(() => {
    if (configFabricId) fetchStudiesList();
  }, [configFabricId]); // eslint-disable-line

  // Re-fetch when tag selection changes — single call after all state updates batch
  useEffect(() => {
    if (configFabricId) fetchStudiesList();
  }, [selectedTagIds]); // eslint-disable-line

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sourceSearchesRef.current && !sourceSearchesRef.current.contains(e.target)) {
        setShowSourceSearchesDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOpenSourceSearch = (sourceSearch) => {
    dispatch(setActiveSearch({ id: sourceSearch.discovery_id, query: sourceSearch.search_query }));
    dispatch(setSLRContext({ included_pmids: sourceSearch.included_pmids }));
    setShowSourceSearchesDropdown(false);
    navigate("/literature-discovery", { state: { fromLinkFullText: true, returnPath: location.pathname } });
  };
 
  const handleMigrateFullText = async (sourceSearch) => {
    setMigrateConfirmSearch(null);
    startProcessing("Migrating full text papers...");
    try {
      const res = await axios.post(
        "/pceapi/slr/discovery/migrate",
        {
          fabric_id: metadata.data_fabric_id,
          discovery_result_ids: sourceSearch.discovery_result_ids,
          migration_type: "full_text",
          search_id: sourceSearch.discovery_id,
        },
        { headers }
      );
      if (res.data?.status === "success") {
        setPopupSuccessMessage(
          <>
            {sourceSearch.included_count} papers ({sourceSearch.full_text_count} with full text) have been queued for migration.
            <br />
            Migration might take some time depending upon the size of papers.
          </>
        );
        setIsSuccessPopupVisible(true);
      } else {
        setPopupErrorMessage(res.data?.message || "Migration failed");
        setIsErrorPopupVisible(true);
      }
    } catch (err) {
      console.error("Migrate error:", err);
      setPopupErrorMessage(err.response?.data?.message || "Failed to migrate papers");
      setIsErrorPopupVisible(true);
    } finally {
      stopProcessing();
    }
  };
 
  // ===== SEARCH HANDLERS =====
  const handleSearch = () => {
    const v = searchInput.trim();
    handleFilterChange("search", v ? v : null);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  // Boundary protection
  useEffect(() => {
    if (totalPages > 0 && filters.page > totalPages) {
      const newFilters = { ...filters, page: totalPages };
      setFilters(newFilters);
      fetchStudiesList(newFilters);
    }
  }, [totalPages]);

  return (
    <div className="h-100 d-flex flex-column">
      {/* ================= TOP BAR ================= */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        {/* LEFT + CENTER */}
        <div className="d-flex w-70 gap-3 align-items-center">
          <p className="ev-med-p-blue ev-bold-med mb-0">Link Full-Text</p>

          <div className="position-relative w-70">
            <input
              type="text"
              className="form-control search-input ms-0"
              placeholder="Search by Study Id, Title, Abstract or Author Name"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
            />
            <button
              className="ev-btn-lightblue-fill-round search-bar-button"
              onClick={handleSearch}
            >
              <i className="bi bi-search"></i>
            </button>
          </div>

        </div>

        {/* RIGHT: Actions */}
        <div className="d-flex gap-2 align-items-center flex-shrink-0">
          {sourceSearches.length > 0 && (
            <div ref={sourceSearchesRef} className="position-relative">
              {/* <button
                className="ev-btn-lightblue-fill-round ev-btn-reg text-nowrap"
                onClick={() => setShowSourceSearchesDropdown((prev) => !prev)}
              >
                <span className="ev-bold-reg">View in Discovery ({sourceSearches.length})</span>
              </button> */}

              {showSourceSearchesDropdown && (
                <div className="slr-source-searches-dropdown p-3">
                  <p className="ev-small-p-dark ev-bold-med mb-2">Source Literature Searches</p>
                  <div className="scrollable-element vh-40">
                    {sourceSearches.map((s) => (
                      <div
                        key={s.discovery_id}
                        className="ev-card-2 ev-radius-xsmall mb-2 p-2 px-3"
                      >
                        <p className="ev-small-p-dark ev-bold-med mb-2 text-truncate">
                          {s.search_query}
                        </p>

                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="ev-message-card3 ev-btn-xsmall ev-radius-xsmall">
                            {s.included_count} included paper(s)
                          </span>
                          <button
                            className="ev-btn-blue-outline-round ev-btn-xsmall text-nowrap ev-bold-med"
                            onClick={() => handleOpenSourceSearch(s)}
                          >
                            View in Discovery
                            <i className="bi bi-arrow-right-circle ms-1"></i>
                          </button>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <span className="category-badge category-badge-1 ev-btn-xsmall ev-radius-xsmall border-0 mb-0 ev-bold-med">
                            {s.full_text_count || 0} paper(s) with full text
                          </span>
                          <button
                            className="ev-btn-blue-fill-round ev-btn-xsmall text-nowrap ev-bold-med py-1"
                            onClick={() => setMigrateConfirmSearch(s)}
                            disabled={!s.discovery_result_ids || s.discovery_result_ids.length === 0}
                          >
                            Migrate to Project<i className="bi bi-box-arrow-in-right ms-1"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="d-flex gap-1 ev-card-4 p-1">
            <button
              className="ev-btn-find-matches ev-btn-small text-nowrap"
              onClick={requestFindMatches}
              disabled={isProcessingPopupVisible}
            >
              Find Matches from Upload
            </button>

            <InlineSelect
              width={130}
              value={String(aiMatchScope)}
              placeholder="All"
              options={[
                {
                  value: "true",
                  label: "All",
                  disabled: (metadata?.WithDecisions || 0) > 0,
                  title: (metadata?.WithDecisions || 0) > 0 ? "Blocked: full-text screening decisions exist" : undefined,
                  onSelect: () => setAiMatchScope(true),
                },
                { value: "false", label: "Unlinked", onSelect: () => setAiMatchScope(false) },
              ]}
            />
          </div>

          <button
            className="ev-btn-lightblue-fill-round ev-btn-reg"
            onClick={handleRefresh}
            disabled={loading}
          >
            <i className="bi bi-arrow-repeat"></i> Refresh
          </button>
        </div>
      </div>

      {/* ================= BODY ================= */}
      <div className="flex-grow-1 overflow-hidden">
        <div className="row gx-3 h-100">
          {/* ========== LEFT: FILTERING PANEL ========== */}
          <div className={filtersCollapsed ? "col-auto" : "ev-filter-panel-fixed"}>
            <div className={filtersCollapsed ? "" : "vh-65 scrollable-element pe-1"}>

              {/* Filtering header */}
              <div
                className="ev-card-2 ev-radius-xsmall d-flex align-items-center justify-content-between p-1 mb-2 cursor-pointer gap-2 position-relative"
                  onClick={() => setFiltersCollapsed(prev => !prev)}
              >
                  {!filtersCollapsed && (
                    <>
                      <p className="ev-med-p-dark-blue ev-bold-med mb-0 position-absolute start-50 translate-middle-x">Filtering</p>
                      {activeFilterCount > 0 ? (
                        <div></div>
                      ) : <div></div>}
                    </>
                  )}
                  <i className={`bi ${filtersCollapsed ? "bi-funnel px-1" : "bi-chevron-left"} ev-med-p-dark-blue`}></i>
                  {filtersCollapsed && activeFilterCount > 0 && (
                    <span className="ev-message-card3 ev-bold-med ev-radius-large"
                      style={{ fontSize: "var(--px-10)", padding: "0 6px" }}>
                      {activeFilterCount}
                    </span>
                  )}
              </div>

              {!filtersCollapsed && (
                <div className="d-flex flex-column gap-2">

                  {/* SECTION 1 — Basic Filters */}
                  <div className="ev-card-8 ev-radius-small p-2">
                    <div
                      className="d-flex align-items-center gap-2 cursor-pointer"
                      onClick={() => setSection1Open(p => !p)}
                    >
                      <span className="ev-message-card3 ev-bold-med d-flex align-items-center justify-content-center ev-radius-large flex-shrink-0"
                        style={{ width: 22, height: 22, fontSize: "var(--px-10)" }}>
                        1
                      </span>
                      <p className="ev-small-p-dark ev-bold-med mb-0 flex-grow-1">Basic Filters</p>
                      <i className={`bi ${section1Open ? "bi-chevron-up" : "bi-chevron-down"} ev-xsmall-p-dark`}></i>
                    </div>

                    {section1Open && (
                      <div className="mt-2">
                        <p className="ev-xsmall-p-dark ev-bold-med mb-1">Publication Year</p>
                        <YearRangePicker
                          from={filters.start_year || ""}
                          to={filters.end_year || ""}
                          onFromChange={(v) => handleFilterChange("start_year", v || null)}
                          onToChange={(v) => handleFilterChange("end_year", v || null)}
                        />

                        <p className="ev-xsmall-p-dark ev-bold-med mb-1">Category</p>
                        <div className="d-flex gap-2 mb-2">
                  {["ALL", "LINKED", "UNLINKED"].map((cat) => (
                    <button
                      key={cat}
                              className={`ev-btn-light-fill ev-btn-reg flex-grow-1 ${filters.category === cat ? "active" : ""}`}
                      onClick={() => handleCategoryChange(cat)}
                    >
                              {cat === "ALL" ? "All" : cat === "LINKED" ? "Linked" : "Unlinked"}
                    </button>
                  ))}
                </div>
                        {/* Bibliographic — papers added via Full-Text QC → Add Paper (hidden until any exist) */}
                        {bibliographicIds.size > 0 && (
                          <div className="d-flex gap-2 mb-2">
                            <button
                              className={`ev-btn-light-fill ev-btn-reg flex-grow-1 ${filters.category === "BIBLIOGRAPHIC" ? "active" : ""}`}
                              onClick={() => handleCategoryChange("BIBLIOGRAPHIC")}
                            >
                              <i className="bi bi-journal-text pe-1" />
                              Bibliographic
                            </button>
                          </div>
                        )}
              </div>
                    )}
                  </div>

                  {/* SECTION 2 — Tags */}
                  <div className="ev-card-8 ev-radius-small p-2">
                    <div
                      className="d-flex align-items-center gap-2 cursor-pointer"
                      onClick={() => setSection2Open(p => !p)}
                    >
                      <span className="ev-message-card3 ev-bold-med d-flex align-items-center justify-content-center ev-radius-large flex-shrink-0"
                        style={{ width: 22, height: 22, fontSize: "var(--px-10)" }}>
                        2
                      </span>
                      <div className="d-flex align-items-center gap-2 flex-grow-1">
                        <p className="ev-small-p-dark ev-bold-med mb-0">Tags</p>
                        {selectedTagIds?.length > 0 && (
                          <span className="ev-message-card3 ev-btn-xsmall">
                            {selectedTagIds.length} selected
                          </span>
                        )}
                      </div>
                      <i className={`bi ${section2Open ? "bi-chevron-up" : "bi-chevron-down"} ev-xsmall-p-dark`}></i>
                    </div>

                    {section2Open && (
                      <div className="mt-2">
              <TagFilterPanel
                tagOptions={tagOptions}
                selectedTagIds={selectedTagIds}
                onToggleTag={handleTagToggle}
                onSetTagIds={handleSetTagIds}
                          onClearAll={() => setSelectedTagIds([])}
                          tagsHeight="vh-15"
                        />
                      </div>
                    )}
                  </div>

                  {/* SECTION 3 — Advanced Filtering */}
                  <div className="ev-card-8 ev-radius-small p-2">
                    <div
                      className="d-flex align-items-center gap-2 cursor-pointer"
                      onClick={() => setSection3Open(p => !p)}
                    >
                      <span className="ev-message-card3 ev-bold-med d-flex align-items-center justify-content-center ev-radius-large flex-shrink-0"
                        style={{ width: 22, height: 22, fontSize: "var(--px-10)" }}>
                        3
                      </span>
                      <p className="ev-small-p-dark ev-bold-med mb-0 flex-grow-1">Advanced Filtering</p>
                      <div
                        className="form-check form-switch fs-6 mb-0 ms-auto"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newVal = !advancedEnabled;
                          setAdvancedEnabled(newVal);
                          setSection3Open(newVal);
                        }}
                      >
                        <input
                          className="form-check-input cursor-pointer"
                          type="checkbox"
                          checked={advancedEnabled}
                          onChange={() => {}}
                        />
                      </div>
                    </div>

                    {!section3Open && advancedEnabled && advConditions.some(c => c.category || c.value) && (
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <span className="ev-small-p-dark">
                          {advConditions.filter(c => c.category || c.value).length} conditions applied
                        </span>
                      </div>
                    )}

                    {section3Open && (
                      <div className="mt-2">

                        {/* Unified filter rules */}
                        <div>
                          {advConditions.map((cond, idx) => (
                            <div key={cond.id}>
                              {idx > 0 && (
                                <div className="d-flex justify-content-center mb-2">
                                  <InlineSelect
                                    id={`${cond.id}:connector`}
                                    openId={advOpenDropdown}
                                    setOpenId={setAdvOpenDropdown}
                                    width={90}
                                    value={cond.connector || "AND"}
                                    placeholder="AND"
                                    options={["AND", "OR"].map((v) => ({
                                      value: v,
                                      label: v,
                                      onSelect: () => updateAdvCondition(cond.id, "connector", v),
                                    }))}
                                  />
                                </div>
                              )}
                              <div className="d-flex align-items-start gap-1 mb-2">
                                <InlineSelect
                                  id={`${cond.id}:field`}
                                  openId={advOpenDropdown}
                                  setOpenId={setAdvOpenDropdown}
                                  className="flex-grow-1"
                                  value={cond.category}
                                  placeholder="Field"
                                  options={ADV_FIELD_OPTIONS.map((o) => ({
                                    ...o,
                                    onSelect: () => updateAdvCondition(cond.id, "category", o.value),
                                  }))}
                                />
                                <InlineSelect
                                  id={`${cond.id}:operator`}
                                  openId={advOpenDropdown}
                                  setOpenId={setAdvOpenDropdown}
                                  className="flex-grow-1"
                                  value={cond.filterOp || "contains"}
                                  placeholder="Contains"
                                  options={ADV_OPERATOR_OPTIONS.map((o) => ({
                                    ...o,
                                    onSelect: () => updateAdvCondition(cond.id, "filterOp", o.value),
                                  }))}
                                />
                                {!["is_empty", "is_not_empty", "is_null", "is_not_null"].includes(cond.filterOp) && (
                                  <input
                                    type="text"
                                    className="formField4 ev-btn-small ev-radius-xsmall flex-grow-1"
                                    placeholder="Value"
                                    value={cond.value}
                                    onChange={(e) => updateAdvCondition(cond.id, "value", e.target.value)}
                                  />
                                )}
                                {advConditions.length > 1 && (
                                  <button
                                    className="ev-btn-light-fill ev-btn-xsmall ev-radius-xsmall border-0"
                                    onClick={() => removeAdvCondition(cond.id)}
                                  >
                                    <i className="bi bi-x-lg"></i>
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                          <button
                            className="ev-btn-text-only-fill ev-btn-small p-0"
                            onClick={addAdvCondition}
                          >
                            <i className="bi bi-plus-lg me-1"></i> Add rule
                          </button>
                        </div>

                        <div className="d-flex gap-1 mt-3">
                          <button
                            className="ev-btn-blue-outline-round ev-btn-reg ev-radius-small w-50"
                            onClick={() => setAdvConditions([{ id: 1, category: "", filterOp: "contains", connector: "AND", value: "" }])}
                          >
                            Clear
                          </button>
                          <button
                            className="ev-btn-blue-fill-round ev-btn-reg ev-radius-xsmall w-50"
                            onClick={() => fetchStudiesList()}
                          >
                            Apply Filters
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              )}
            </div>
          </div>

          {/* ========== RIGHT: LIST PANEL ========== */}
          <div className="col h-65 d-flex flex-column">
            {/* ================= SUMMARY ================= */}
            <div className="mb-2">
              <div className="ev-card-8 d-flex align-items-center ev-radius-small mb-2 overflow-hidden px-2">
                  <div className="d-flex flex-column align-items-center text-center flex-grow-1 py-2">
                    <span className="ev-bold-large text-primary">{metadata.total_studies || 0}</span>
                    <span className="ev-bold-reg text-primary">Results</span>
                  </div>
                  <div className="vr my-2"></div>
                  <div className="d-flex flex-column align-items-center text-center flex-grow-1 py-2">
                    <span className="ev-bold-large text-success">{metadata.Linked || 0}</span>
                    <span className="ev-bold-reg text-success">With Full Text</span>
                  </div>
                  <div className="vr my-2"></div>
                  <div className="d-flex flex-column align-items-center text-center flex-grow-1 py-2">
                    <span className="ev-bold-large text-muted">{metadata.Unlinked || 0}</span>
                    <span className="ev-bold-reg text-muted">Without Full Text</span>
                  </div>
                  {unsavedCount > 0 && (
                    <>
                      <div className="vr my-2"></div>
                      <div className="d-flex flex-column align-items-center text-center flex-grow-1 py-2">
                        <span className="ev-bold-large text-warning">{unsavedCount}</span>
                        <span className="ev-bold-reg text-warning">Unsaved</span>
                      </div>
                    </>
                  )}
              </div>

              {unsavedCount > 0 && (
                <div className="text-end mt-2 mb-2">
                  <button
                    className="ev-btn-yellow-fill-round ev-btn-small text-nowrap"
                    onClick={requestSaveAllMappings}
                    disabled={isProcessingPopupVisible}
                  >
                    Save All Selection
                  </button>
                </div>
              )}
            </div>

            <div className="scrollable-element flex-grow-1 pe-1 vh-50">
              {Array.isArray(safeStudies) &&
                safeStudies.map((item) => {
                  const unsavedData = unsavedMappings[item.phase1_id];
                  const isLinked = item.doc_id_linked !== null;
                  const hasUnsaved = !!unsavedData;
                  const isAIMatched = unsavedData?.isAIMatched || false;

                  return (
                    <div
                      key={item.phase1_id}
                      className={`ev-card-8 ev-radius-reg mb-2 py-2 px-3${
                        selectedId === item.phase1_id || hoveredId === item.phase1_id ? " active" : ""
                      }`}
                      onClick={() => setSelectedId(item.phase1_id)}
                      onMouseEnter={() => setHoveredId(item.phase1_id)}
                      onMouseLeave={() => setHoveredId(null)}
                    >
                      <div className="d-flex justify-content-between">
                        {/* LEFT */}
                        <div className="d-flex flex-column gap-1">
                          <div className="d-flex gap-2 align-items-center flex-nowrap">
                            <span className="ev-message-card5 ev-btn-small ev-radius-xsmall">
                              ID. {item.id}
                            </span>
                            <span className="ev-message-card3 ev-btn-small ev-radius-xsmall">
                              Year {item.year || "N/A"}
                            </span>
                            {bibliographicIds.has(String(item.study_id ?? item.id)) && (
                              <span className="ev-message-card3 ev-btn-small ev-radius-xsmall text-nowrap">
                                <i className="bi bi-journal-text pe-1" />
                                Bibliographic
                              </span>
                            )}
                            <span className="ev-decision-card included">
                              <span className="ev-decision-icon included">✓</span>
                              Include
                            </span>
                          </div>
                          <p className="ev-bold-med ev-small-p-blue mt-2 mb-0">
                            {item.title || "Untitled"}
                          </p>
                          <p className="ev-xsmall-p-dark mb-0">
                            <i>Authors. {item.authors || "Unknown"}</i>
                          </p>
                        </div>

                        {/* RIGHT */}
                        <div className="d-flex align-items-start gap-1">
                          {isAIMatched && (
                            <span className="category-badge category-badge-8">
                              Matched with AI
                            </span>
                          )}
                          {hasUnsaved && (
                            <span className="category-badge category-badge-3 ev-btn-small mb-0 ev-radius-xsmall border-0 pe-3">
                              Unsaved
                            </span>
                          )}
                          {isLinked && !hasUnsaved && (
                            <div className="category-badge category-badge-1 d-flex align-items-center gap-2 ps-2 py-1 pe-1 border-0 text-nowrap flex-nowrap ev-radius-xsmall">
                              <span>Full Text</span>
                              <span className="ev-message-card6 ev-fs-10 py-0 px-1 text-nowrap me-1">
                                <TrimContent
                                  text={
                                    item.s3_path
                                      ? item.s3_path.split("/").pop()
                                      : `Doc_${item.doc_id_linked}`
                                  }
                                  maxLength={15}
                                />
                                <button
                                  className="ev-btn-light-fill ev-btn-xsmall ev-bold-reg ms-1 me-1 my-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenPreview({
                                      doc_id: item.doc_id_linked,
                                      name: item.s3_path
                                        ? item.s3_path.split("/").pop()
                                        : `Doc_${item.doc_id_linked}`,
                                    });
                                  }}
                                >
                                  Preview
                                </button>
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-1 w-100">
                        {/* LEFT: Abstract */}
                        <div
                          className="d-flex align-items-center gap-1 ev-small-p-blue ev-bold-med cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAbstract(item.phase1_id);
                          }}
                        >
                          <span>Abstract</span>
                          <i
                            className={`bi bi-chevron-${
                              expandedAbstracts[item.phase1_id] ? "up" : "down"
                            } ev-fs-12`}
                          ></i>
                        </div>

                        {/* RIGHT: Upload actions */}
                        <div className="d-flex gap-2 align-items-center flex-nowrap">
                          <button
                            className="ev-btn-white-fill-round ev-btn-small text-nowrap border border-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenBulkModal(item);
                            }}
                          >
                            Select From Bulk Uploads
                          </button>

                          <div className="category-badge category-badge-5 d-flex align-items-center gap-2 py-1 ps-2 pe-1 mb-0">
                            <span className="text-nowrap ev-fs-11">Upload From Device</span>
                            <button
                              className="ev-btn-red-fill-round ev-btn-xsmall text-nowrap"
                              onClick={(e) => {
                                e.stopPropagation();
                                fileInputRef.current.targetStudy = item;
                                fileInputRef.current.click();
                              }}
                            >
                              <i className="bi bi-filetype-pdf me-1"></i>
                              Upload
                            </button>
                          </div>
                        </div>
                      </div>

                      {expandedAbstracts[item.phase1_id] && (
                        <div className="mt-2 p-2 ev-card-8 ev-radius-xsmall">
                          <p className="ev-xsmall-p-dark mb-0">
                            {(!item.abstract || item.abstract === "nan") ? "Not Available" : item.abstract}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>

            {/* ================= PAGE NAVIGATION ================= */}
            <div className="d-flex justify-content-between align-items-center mt-2">
              <span className="ev-xsmall-p-dark">
                Showing {safeStudies.length} of {metadata.total_studies || 0} Results
              </span>

              <div className="d-flex align-items-center gap-2">
                <button
                  className="ev-btn-light-fill ev-btn-xsmall"
                  disabled={filters.page === 1}
                  onClick={handlePagePrev}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                <button className="ev-btn-light-fill ev-btn-xsmall" disabled>
                  Page {filters.page} of {totalPages || 1}
                </button>
                <button
                  className="ev-btn-light-fill ev-btn-xsmall"
                  disabled={filters.page >= totalPages}
                  onClick={handlePageNext}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>

              <div className="text-end">
                <PaginationControls
                  initialSize={filters.size}
                  onSizeChange={handlePageSizeChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden file input for device upload */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".pdf"
        onChange={(e) => {
          const targetStudy = fileInputRef.current.targetStudy;
          handleFileUploadFromDevice(e, targetStudy);
        }}
      />

      {/* ===== BULK MODAL ===== */}
      {showBulkModal && bulkModalStudy && (
        <div
          className="modal fade show d-block ev-modal-overlay"
          onClick={() => {
            setShowBulkModal(false);
            setSelectedDocId(null);
          }}
        >
          <div
            className="modal-dialog modal-dialog-centered w-40 modal-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content ev-message-card2 ev-card-3 ev-radius-med">
              {/* HEADER */}
              <div className="ev-card-2 pb-0 mb-3">
                <span className="ev-message-card6 ev-radius-xsmall ev-bold-reg mb-1">
                  Abstract ID. {bulkModalStudy.id}
                </span>
                <p className="ev-bold-reg mb-1">Title.</p>
                <p className="ev-reg-p-dark ev-fs-12">
                  {bulkModalStudy.title || "Untitled"}
                </p>
              </div>

              {/* SELECT ROW */}
              <div className="d-flex justify-content-between align-items-center mb-1">
                <p className="ev-med-p-blue mb-0">
                  Select Document ({bulkDocsPagination.total_docs})
                </p>
                <div className="d-flex gap-2 align-items-center">
                  {unsavedMappings[bulkModalStudy.phase1_id]?.isAIMatched && (
                    <span className="category-badge category-badge-8">
                      Matched with AI
                    </span>
                  )}
                  {selectedDocId && (
                    <span className="ev-btn-yellow-fill-round ev-radius-xsmall ev-btn-xsmall">
                      Unsaved
                    </span>
                  )}
                  <button
                    className="ev-btn-blue-outline-round ev-btn-xsmall"
                    disabled={!selectedDocId}
                    onClick={() => {
                      const selectedDoc = bulkDocs.find(
                        (d) => d.doc_id === selectedDocId,
                      );
                      if (selectedDoc) {
                        handleSelectDocument(selectedDoc);
                        setSelectedDocId(null);
                      }
                    }}
                  >
                    Save Selection
                  </button>
                </div>
              </div>

              {/* SEARCH BAR */}
              <div className="position-relative d-flex align-items-center w-50 mb-2">
                <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3"></i>
                <input
                  type="text"
                  className="form-control search-input"
                  placeholder="search document name"
                  value={bulkSearchInput}
                  onChange={(e) => setBulkSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") fetchBulkDocs(bulkModalStudy, bulkSearchInput, 0, false);
                  }}
                />
              </div>

              {/* DOCUMENT LIST */}
              <div className="scrollable-element vh-40">
                {bulkDocs.map((doc) => {
                  const isSelected = selectedDocId === doc.doc_id;
                  return (
                    <div
                      key={doc.doc_id}
                      className={`ev-radius-xsmall mb-2 d-flex justify-content-between align-items-start p-2 cursor-pointer gap-1 ${
                        isSelected
                          ? "ev-btn-red-fill-round selected"
                          : "category-badge category-badge-5 border-0"
                      }`}
                      onClick={() => setSelectedDocId(doc.doc_id)}
                    >
                      <div className="d-flex gap-2 flex-grow-1 overflow-hidden">
                        <i
                          className={`bi bi-file-earmark-pdf-fill ev-fs-20 flex-shrink-0 ${
                            isSelected ? "text-white" : "text-danger"
                          }`}
                        ></i>
                        <div className="overflow-hidden">
                          <p
                            className={`ev-bold-reg mb-0 text-break ${isSelected ? "text-white" : "ev-reg-p-dark"}`}
                          >
                            {doc.name}
                          </p>
                          <p
                            className={`ev-xsmall-p-blue mb-0 ${isSelected ? "text-white" : ""}`}
                          >
                            {doc.title || doc.name}
                          </p>
                        </div>
                      </div>

                      <div className="d-flex gap-2 flex-shrink-0">
                        <button
                          className="ev-btn-white-fill-round ev-btn-small border border-danger ev-reg-p-red ev-bold-med"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenPreview(doc);
                          }}
                        >
                          Preview
                        </button>
                        <button
                          className="ev-btn-red-fill-round ev-btn-xsmall ev-radius-xsmall"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDocToDelete(doc);
                            setShowDeleteConfirm(true);
                          }}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* FOOTER */}
              <div className="text-end mt-3">
                {bulkDocsPagination.has_more && (
                  <button
                    className="ev-btn-blue-outline-round ev-btn-small pe-2"
                    onClick={loadMoreBulkDocs}
                    disabled={bulkDocsLoading}
                  >
                    {bulkDocsLoading ? "Loading..." : "Load More"}
                  </button>
                )}
                <button
                  className="ev-btn-blue-outline-round ms-2 ev-btn-small"
                  onClick={() => {
                    setShowBulkModal(false);
                    setBulkModalStudy(null);
                    setSelectedDocId(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>

          {/* ===== DELETE CONFIRMATION MODAL ===== */}
          <DeleteConfirmationModal
            show={showDeleteConfirm}
            onClose={() => {
              setShowDeleteConfirm(false);
              setDocToDelete(null);
            }}
            onConfirm={handleConfirmDeleteBulkDoc}
            objectName="document"
            itemName={docToDelete?.name || docToDelete?.title}
            bodyContent={
              <div className="ev-fs-11 text-muted">
                This action will only remove the document from the current selection.
              </div>
            }
          />
        </div>
      )}

      <DocumentPreview
        isOpen={previewOpen}
        onClose={handleClosePreview}
        docId={previewDocId}
        documentName={previewDocName}
        bucket_type="input"
      />

      <ConfirmationModal
        show={!!migrateConfirmSearch}
        onClose={() => setMigrateConfirmSearch(null)}
        onConfirm={() => handleMigrateFullText(migrateConfirmSearch)}
        title="Migrate Full Text Papers"
        bodyContent={
          migrateConfirmSearch && (
            <p className="mb-0">
              Are you sure you want to migrate{" "}
              <strong>{migrateConfirmSearch.full_text_count || 0} papers</strong> with full
              text from <strong>{(migrateConfirmSearch.search_query || "").slice(0, 25)}
                {migrateConfirmSearch.search_query?.length > 15 ? "..." : ""}</strong> to{" "}
              <strong>{projectName}</strong>?
            </p>
          )
        }
        confirmButtonText="Migrate"
        confirmButtonClass="btn-primary"
      />

      <ConfirmationModal
        show={!!pendingDocSelect}
        onClose={() => setPendingDocSelect(null)}
        onConfirm={() => {
          const p = pendingDocSelect;
          setPendingDocSelect(null);
          if (p) applySelectDocument(p.doc, p.study);
        }}
        title="Change Linked Document"
        bodyContent={
          pendingDocSelect && (
            <div>
              <p className="mb-2">
                Select <strong>{pendingDocSelect.doc.name || pendingDocSelect.doc.title}</strong> for
                Study ID <strong>{pendingDocSelect.study.id}</strong>?
              </p>
              {pendingDocSelect.changingDoc && (
                <p className="mb-2">
                  This paper is already linked to another document, which will be replaced
                  when you save.
                </p>
              )}
              {pendingDocSelect.loseDecisions && (
                <p className="mb-2 text-danger">
                  All full-text screening decisions for this paper (human, AI, conflict and QC)
                  will be permanently deleted when you save.
                </p>
              )}
              {pendingDocSelect.otherStudies.length > 0 && (
                <p className="mb-0 text-danger">
                  This document is already linked to Study ID{" "}
                  {pendingDocSelect.otherStudies.map((s) => s.study_id).join(", ")}
                  {pendingDocSelect.otherStudies.some((s) => s.has_fulltext_decisions)
                    ? " (which already has full-text decisions)"
                    : ""}
                  . The same PDF would then be linked to two papers.
                </p>
              )}
            </div>
          )
        }
        confirmButtonText="Select Document"
        confirmButtonClass="btn-danger"
      />

      <ConfirmationModal
        show={docChangeConfirmCount > 0}
        onClose={() => setDocChangeConfirmCount(0)}
        onConfirm={() => {
          setDocChangeConfirmCount(0);
          handleSaveAllMappings();
        }}
        title="Change Linked Documents"
        bodyContent={
          <>
            <p className="mb-2">
              You are changing the linked document for{" "}
              <strong>{docChangeConfirmCount} paper(s)</strong> that already
              have full-text screening decisions.
            </p>
            <p className="mb-0 text-danger">
              All full-text screening decisions for these papers (human, AI,
              conflict resolution and QC) will be permanently deleted, and
              full-text QC will need to be completed again. Do you want to continue?
            </p>
          </>
        }
        confirmButtonText="Change & Delete Decisions"
        confirmButtonClass="btn-danger"
      />

      { }
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage || "Processing..."} />
      )}

      {isErrorPopupVisible && (
        <ErrorPopup message={popupErrorMessage} onClose={closeErrorPopup} />
      )}

      {isSuccessPopupVisible && (
        <SuccessPopup
          message={popupSuccessMessage}
          onClose={closeSuccessPopup}
        />
      )}
    </div>
  );
};

export default LinkFullText;